<template>
	<div>
		<div class="flex justify-center mb-4" v-if="(currentSnapshot && enablePreview) || $slots.currentSnapshot">
			<slot name="currentSnapshot">
				<BaseImage :src="currentSnapshot" :alt="$t('overview_image')" class="mb-4 h-auto md:h-56" />
			</slot>
		</div>
		<div :class="{ 'md:flex sm:space-x-4 flex-wrap justify-center': !$slots.takeImage, 'w-full h-16': appMode }" class="cursor-pointer">
			<div @click="openCamera" v-if="takeImage && isAppContainer">
				<slot name="takeImage">
					<BaseButton :disabled="disableButtons" class="w-full sm:w-auto" style="min-width: 320px; max-width: 320px">
						<font-awesome-icon :icon="['fas', 'camera']" class="w-5 h-5 pr-1" size="lg" />
						{{ takeImageLabel }}
					</BaseButton>
				</slot>
			</div>

			<slot name="uploadImage" v-if="!removeUploadButton">
				<BaseButton
					@click.stop="openUpload"
					:disabled="disableButtons"
					class="w-full sm:w-auto mt-4 sm:mt-0"
					style="min-width: 250px; max-width: 250px"
				>
					<input type="file" ref="file" :accept="accept" @change="chosedUpload" class="hidden" />
					<font-awesome-icon :icon="['fas', 'image']" class="w-5 h-5 pr-1" size="lg" />
					{{ label }}
				</BaseButton>
			</slot>
		</div>
		<BaseSelect
			ref="BaseSelect.camera"
			:required="true"
			label="Kamera"
			options-label="label"
			:options="possibleCameras"
			v-if="possibleCameras.length > 1"
			v-model="currentCamera"
		/>

		<div v-if="successMessage && !disableSuccessMessage">{{ successMessage }}</div>
	</div>
</template>

<script>
import BaseButton from '@/components/forms/buttons/BaseButton';
import BaseSelect from '@/components/forms/inputs/BaseSelect';
import LoadingSpinner from '@/mixins/LoadingSpinner';
import AppContainerMixin from '@/mixins/AppContainerMixin';
import BaseImage from '@/components/forms/BaseImage';

function emitScanResult(event) {
	if (event.type === 'snapshot') {
		window.appcontainer.component.currentSnapshot = event.detail;
		window.appcontainer.component.emitSnapshot();
	} else {
		window.appcontainer.component.currentSnapshot = event.detail.base64Image;
		window.appcontainer.component.$emit(event.type, event.detail);
	}

	window.removeEventListener(event.type, emitScanResult);
	window.appcontainer.component.setLoadingStatus(false);
	window.appcontainer.component = null;
}

function startLoadingSpinner(event) {
	window.appcontainer.component.setLoadingStatus(true);
	window.removeEventListener(event.type, emitScanResult);
}

export default {
	name: 'BaseCamera',
	mixins: [LoadingSpinner, AppContainerMixin],
	props: {
		orientation: {},
		takeImageLabel: {
			default: function() {
				return this.$t('activate_camera');
			},
		},
		takeImage: { default: false },
		label: {
			default: function() {
				return this.$t('add_photo');
			},
		},
		scanVehicleRegistration: { type: Boolean, default: false },
		disableButtons: { type: Boolean, default: false },
		removeUploadButton: {},
		disableSuccessMessage: {},
		closeOnSnapshot: { default: true, type: Boolean },
		enablePreview: {
			default: true,
			type: Boolean,
		},
		accept: { default: 'image/*' },
		appMode: { default: false },
		allowUploadInAppContainer: { default: true },
		askForOverwrite: { default: false, type: Boolean },
		allowMultiplePhotos: { default: false },
	},
	data() {
		return {
			possibleCameras: [],
			currentCamera: { deviceId: null },
			isHidden: true,
			successMessage: '',
			currentSnapshot: null,
		};
	},
	components: {
		BaseImage,
		BaseSelect,
		BaseButton,
	},
	methods: {
		openUpload() {
			if (this.isAppContainer) {
				this.openCamera();
			} else {
				this.$refs.file.click();
			}
		},
		emitSnapshot() {
			if (this.orientation && this.orientation === 'landscape') {
				const image = document.createElement('img');

				image.addEventListener('load', () => {
					if (image.width > image.height) {
						this.$emit('snapshot', this.currentSnapshot);
					} else {
						this.currentSnapshot = null;
						this.$swal(this.$t('general.attention').toString(), this.$t('use_landscape_image').toString(), 'warning');
					}
				});
				if (this.currentSnapshot.includes('|') && this.isAppContainer) {
					//TODO: maybe iterate through whole array?!
					image.src = this.currentSnapshot.split('|')[0];
				} else {
					image.src = this.currentSnapshot;
				}
			} else {
				this.$emit('snapshot', this.currentSnapshot);
			}
		},
		openCamera() {
			window.appcontainer.component = this;
			if (this.allowMultiplePhotos) {
				window.appcontainer.takeMultiplePictures();
			}

			if (this.askForOverwrite) {
				this.$swal({
					title: this.$t('sure').toString(),
					text: this.$t('overwrite_file').toString(),
					icon: 'warning',
					cancelButtonText: this.$t('general.cancel').toString(),
					showCancelButton: true,
					confirmButtonText: this.$t('overwrite').toString(),
				}).then(result => {
					if (result.value) {
						if (this.scanVehicleRegistration) {
							window.appcontainer.startScan();
							window.addEventListener('scanResult', emitScanResult);
							window.addEventListener('startLoadingSpinner', startLoadingSpinner);
						} else if (this.allowUploadInAppContainer) {
							window.appcontainer.takeOrUploadPicture();
							window.addEventListener('snapshot', emitScanResult);
						} else {
							window.appcontainer.takePicture();
							window.addEventListener('snapshot', emitScanResult);
						}
					}
				});
			} else {
				if (this.scanVehicleRegistration) {
					window.appcontainer.startScan();
					window.addEventListener('scanResult', emitScanResult);
					window.addEventListener('startLoadingSpinner', startLoadingSpinner);
				} else if (this.allowUploadInAppContainer) {
					window.appcontainer.takeOrUploadPicture();
					window.addEventListener('snapshot', emitScanResult);
				} else {
					window.appcontainer.takePicture();
					window.addEventListener('snapshot', emitScanResult);
				}
			}
		},
		chosedUpload() {
			const file = this.$refs.file.files[0];
			const reader = new FileReader();

			//TODO: dont know if we need this
			// if (!file.type.startsWith('image/')) {
			// 	return false;
			// }

			reader.onload = e => {
				this.currentSnapshot = e.target.result;
				this.emitSnapshot();
			};
			reader.readAsDataURL(file);
		},
		snapshotButtonClick() {
			this.currentSnapshot = this.$refs.webcam.capture();
			this.emitSnapshot();
			this.successMessage = 'Das Bild wurde erfolgreich empfangen.';
			if (this.closeOnSnapshot) {
				this.isHidden = true;
				this.$refs.webcam.close();
			}
		},
	},
};
</script>
